import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";

interface AppShellProps {
  children: ReactNode;
  /**
   * Short label used in the fallback UI to identify which entry crashed,
   * e.g., "the taskpane" or "the sign-in flow". Becomes part of the user-facing
   * error message.
   */
  routeLabel: string;
}

function ErrorFallback({
  error,
  resetError,
  routeLabel,
}: {
  error: unknown;
  resetError: () => void;
  routeLabel: string;
}) {
  const msg = error instanceof Error ? error.message : "Unknown error";
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex flex-col gap-2 max-w-md">
        <h1 className="text-lg font-semibold text-[var(--text-primary)]">
          Something went wrong
        </h1>
        <p className="text-sm text-[var(--text-secondary)]">
          We hit an unexpected error in {routeLabel}. The error has been reported automatically.
        </p>
        <p className="text-xs text-[var(--text-muted)] font-mono break-all mt-2">
          {msg}
        </p>
      </div>
      <button
        type="button"
        onClick={resetError}
        className="rounded-md bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] hover:opacity-90"
      >
        Try again
      </button>
    </div>
  );
}

export function AppShell({ children, routeLabel }: AppShellProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} routeLabel={routeLabel} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
